import { zodResolver } from "@hookform/resolvers/zod";
import { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { faBriefcase, faGlobe } from "@fortawesome/pro-regular-svg-icons";

import { Icon, Modal } from "@/components";

import { useUpdateCompaniesMutation } from "@/pages/Private/redux/companies/companies.api";

import FormTextField from "../../Account/components/FormTextField";
import { Companies, UpdateCompanies, UpdateCompaniesSchema } from "../schema/companies";

interface updateCompaniesModalProps {
	companies: Companies;
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const UpdateCompaniesModal: FunctionComponent<updateCompaniesModalProps> = ({
	companies,
	isOpen,
	setIsOpen,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`companies.edit.${key}`);
	const [update, { isLoading, error }] = useUpdateCompaniesMutation();

	const {
		handleSubmit,
		register,
		formState: { errors },
	} = useForm<UpdateCompanies>({
		defaultValues: {
			name: companies.name,
			website: companies.website || "",
		},
		resolver: zodResolver(UpdateCompaniesSchema),
	});

	const onSubmit = async (values: UpdateCompanies) => {
		try {
			await update({ ...values, id: companies.id }).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			aboveHeader={<Icon className="w-4 h-4" icon={faBriefcase} />}
			dialogContentClassName="!p-[2px]"
			formError={error}
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.save")}
			subtitle={ts("subtitle")}
			title={ts("title")}
		>
			<FormTextField
				errorMessage={errors?.name?.message}
				formRegister={register("name")}
				label={t("companies.name")}
				startAdornment={
					<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faBriefcase} />
				}
			/>
			<FormTextField
				errorMessage={errors?.website?.message}
				formRegister={register("website")}
				label={t("companies.website")}
				required={false}
				startAdornment={<Icon className="w-4 h-4 mr-2 text-primary-gray-lighter" icon={faGlobe} />}
			/>
		</Modal>
	);
};
